import {
  Component,
  h,
  Prop,
  State,
  ComponentInterface,
  Element,
} from "@stencil/core";
import { Unsubscribe, store } from "@stencil/redux";
import { ConfirmNotification, INotification } from "../../../models";
import {
  CommonService,
  DeviceService,
  Log,
  TranslateService,
} from "../../../services";
import { NotificationService } from "../../../services/notification.service";
import { dispatchDismiss } from "../../../store/actions/notification";
import { NotificationTypeEnum } from "../../../models/enums";
import * as FocusTrap from "focus-trap";

@Component({
  tag: "av-notification",
  styleUrl: "av-notification.scss",
  shadow: true,
})
export class AvNotification implements ComponentInterface {
  @Prop() notification: INotification;
  @State() open = false;
  @Element() hostRef: HTMLElement;

  timeoutHide: number;
  timeoutDestroy: number;
  storeUnsubscribe: Unsubscribe;
  dispatchDismiss: typeof dispatchDismiss;

  waitForAnimationCompletionMs = 300;

  supportsBackdropBlur = false;
  cancelBtn: HTMLAvButtonElement;
  approveBnt: HTMLAvButtonElement;
  trap: FocusTrap.FocusTrap;

  constructor() {
    this.supportsBackdropBlur = !DeviceService.isFirefox;
  }

  componentWillLoad() {
    store.mapDispatchToProps(this, {
      dispatchDismiss,
    });
  }

  componentDidLoad() {
    this.open = true;
    // set timeout for the animation to happen on close
    if (this.notification.options?.ttl > 0) {
      this.timeoutHide = CommonService.timer.setTimeout(
        (_) => (this.open = false),
        this.notification.options.ttl - this.waitForAnimationCompletionMs
      );
      this.timeoutDestroy = CommonService.timer.setTimeout(
        (_) => this.dispatchDismiss(this.notification.id),
        this.notification.options.ttl
      );
    }

    setTimeout(() => {
      this.cancelBtn?.setFocus();
    }, 100);
  }

  cardReady(el) {
    if (this.notification.type !== NotificationTypeEnum.confirm) {
      return;
    }
    if (this.trap) {
      this.trap.updateContainerElements(el);
      return;
    }
    this.trap = FocusTrap.createFocusTrap(el, {
      tabbableOptions: {
        getShadowRoot: true,
      },
    });
    setTimeout(() => {
      try {
        this.trap.activate();
      } catch (ex) {
        Log.error(ex);
      }
    }, 100);
  }

  disconnectedCallback() {
    this.open = false;
    this.clearTimers();
    if (this.notification.type === NotificationTypeEnum.confirm) {
      NotificationService.confirmComplete(this.notification.id);
      this.trap?.deactivate();
    }
  }

  clearAndDismiss() {
    if (this.isConfirm) {
      return;
    }
    this.clearTimers();
    NotificationService.broadcastClicked(this.notification);
    this.dismiss();
  }

  dismiss() {
    NotificationService.confirmComplete(this.notification.id);
    this.open = false;
    // wait for the animation to finish
    CommonService.timer.setTimeout(
      (_) => this.dispatchDismiss(this.notification.id),
      this.waitForAnimationCompletionMs
    );
  }

  clearTimers() {
    if (this.timeoutHide) {
      CommonService.timer.clearTimeout(this.timeoutHide);
      this.timeoutHide = undefined;
    }
    if (this.timeoutDestroy) {
      CommonService.timer.clearTimeout(this.timeoutDestroy);
      this.timeoutDestroy = undefined;
    }
  }

  private get isConfirm() {
    return this.notification.type === NotificationTypeEnum.confirm;
  }

  private dismissConfirm() {
    this.dismiss();
    (this.notification as ConfirmNotification).cancel();
  }

  private acceptConfirm() {
    this.dismiss();
    (this.notification as ConfirmNotification).confirm();
  }

  render() {
    const cl = {
      "av-notification": true,
      "av-notification-confirm":
        this.notification.type === NotificationTypeEnum.confirm,
      "av-notification-success":
        this.notification.type === NotificationTypeEnum.success,
      "av-notification-warning":
        this.notification.type === NotificationTypeEnum.warning,
      "av-notification-error":
        this.notification.type === NotificationTypeEnum.error,
      "av-notification-info":
        [
          NotificationTypeEnum.info,
          NotificationTypeEnum.greeting,
          NotificationTypeEnum.confirm,
        ].includes(this.notification.type) && !this.supportsBackdropBlur,
      "av-notification-info-backdrop-blur":
        [
          NotificationTypeEnum.info,
          NotificationTypeEnum.greeting,
          NotificationTypeEnum.confirm,
        ].includes(this.notification.type) && this.supportsBackdropBlur,
    };

    const color = [
      NotificationTypeEnum.success,
      NotificationTypeEnum.error,
    ].includes(this.notification.type)
      ? "light"
      : "theme";

    const cancelText = TranslateService.instant("Cancel");
    const confirmText = TranslateService.instant("Yes");
    const ariaDescId = this.notification.id + "_desc";
    const ariaLabelId = this.notification.id + "_label";
    const dataTestID = `notification/${this.notification.type}`;

    return (
      <av-card
        role={this.isConfirm ? "alertdialog" : "alert"}
        aria-labelledby={this.notification.title ? ariaLabelId : ariaDescId}
        aria-describedby={
          this.notification.title && this.notification.options?.body
            ? ariaDescId
            : null
        }
        aria-modal={this.isConfirm ? "true" : null}
        ignoreTheme={true}
        inline={true}
        data-tid={dataTestID}
        onClick={this.clearAndDismiss.bind(this)}
        open={this.open}
        class={cl}
        ref={this.cardReady.bind(this)}
      >
        <div slot="card-body">
          {!this.isConfirm && (
            <av-button
              aria-label={TranslateService.instant("Dismiss notification")}
              class="av-notification-close"
              size="sm"
              shape="round"
              color="link"
            >
              <av-asset image="close" color={color}></av-asset>
            </av-button>
          )}
          {this.notification.title && (
            <h3 class="av-direction">
              <av-translate id={ariaLabelId}>
                {this.notification.title}
              </av-translate>
            </h3>
          )}
          {this.notification.options?.body && (
            <div class="av-direction">
              <av-translate id={ariaDescId}>
                {this.notification.options.body}
              </av-translate>
            </div>
          )}
        </div>
        {this.isConfirm && (
          <div slot="card-footer" class="av-confirm-footer">
            <av-button
              ariaLabel={cancelText}
              data-tid="confirm/cancel"
              onClick={(_) => this.dismissConfirm()}
              color="basic"
              ref={(el) => (this.cancelBtn = el)}
            >
              {cancelText}
            </av-button>
            <av-button
              ariaLabel={confirmText}
              data-tid="confirm/approve"
              onClick={(_) => this.acceptConfirm()}
              color="accent"
              ref={(el) => (this.approveBnt = el)}
            >
              {confirmText}
            </av-button>
          </div>
        )}
      </av-card>
    );
  }
}
